/**
 * The copy module's PURE half: how a key becomes a sentence, with no store behind it.
 *
 * 🔴 **It is a separate file for exactly one reason, and it is the same reason
 * `utils/currency/format.ts` is one.** `./index` imports core's redux store — that is what
 * `copy()` reads the LANGUAGE from — and importing a module imports everything behind it. A
 * pure model that needs only to turn a key into words would otherwise drag the whole store
 * graph in, `utils/navigation`'s `createBrowserHistory()` included, which is a
 * `document is not defined` in any plain-node context. Twelve model test suites proved it.
 *
 * `./index` re-exports all of this, so `@rista/core/copy` stays the one public entry. Import
 * THIS file directly only when you must not pull the store — and if you are pure, prefer taking
 * a `CoreCopy` as an argument the way you already take a `MoneyFormat`.
 */
import { type Catalogue, type CopyParams, DEFAULT_LANGUAGE, resolveCopy } from "../utils/i18n";

import { en } from "./en";

/** Every key core can draw. A typo is a compile error; so is a missing translation. */
export type CoreCopyKey = keyof typeof en;

/**
 * 🔴 **THE type of a copy parameter.** A pure model takes one of these exactly as it takes a
 * `MoneyFormat` — because it cannot read the store, and the language is runtime data.
 *
 * ⚠️ `packages/ui`'s `KioskCopy` is a DIFFERENT type over a different key set. Neither is
 * assignable to the other and neither should be: the two catalogues are disjoint, and a screen
 * that could pass its own `t` in here would silently resolve nothing.
 */
export type CoreCopy = (key: CoreCopyKey, params?: CopyParams) => string;

/**
 * Keyed by primary SUBTAG, not by full tag — `ar-AE` and `ar-EG` read the same Arabic. Same
 * rule as the kiosk catalogue's, so the two stay in step.
 */
const CATALOGUES: Record<string, Catalogue<CoreCopyKey>> = {
  en,
};

const catalogueFor = (language: string): Catalogue<CoreCopyKey> =>
  CATALOGUES[String(language ?? "").toLowerCase().split("-")[0]] ?? en;

/**
 * Resolve one key in a GIVEN language. The store-backed `copy()` in `./index` is this with the
 * language filled in.
 *
 * Falls back to `en` for a key the active catalogue lacks at runtime, and `resolveCopy` falls
 * back to the key itself after that — a terminal in front of a customer degrades to an ugly
 * string rather than white-screening. The types make both near-unreachable; they exist because
 * "near" is not "never" on hardware nobody is standing beside.
 */
export function resolveCoreCopy(
  key: CoreCopyKey,
  params?: CopyParams,
  language: string = DEFAULT_LANGUAGE,
): string {
  const catalogue = catalogueFor(language);
  const source = key in catalogue ? catalogue : en;
  return resolveCopy(source, key, params, language);
}

/**
 * A `CoreCopy` bound to one language — what a test, a rendercheck or any other store-free caller
 * hands to a pure model.
 *
 * `makeCoreCopy()` with no argument is English, which is why a pure model's test needs no
 * provider and no mock to assert a real sentence.
 */
export const makeCoreCopy =
  (language: string = DEFAULT_LANGUAGE): CoreCopy =>
  (key, params) =>
    resolveCoreCopy(key, params, language);

export { en };
